import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, MessageSquare, Save, Smartphone, CalendarClock, Activity } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';

// Defined at module scope on purpose. Declaring a component inside another
// creates a brand-new component type on every render, so React unmounts and
// remounts it each time instead of updating it — losing any state it holds and
// restarting its transitions mid-animation.
const PremiumToggle = ({ isActive, onClick }) => (
  <div className="flex items-center gap-2.5">
    <span className={`w-16 text-right text-[10px] font-bold uppercase tracking-wider transition-colors duration-300 ${isActive ? 'text-[var(--theme-primary)]' : 'text-slate-400'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
    <button
      onClick={onClick}
      className={`w-12 h-6 rounded-full transition-all duration-300 relative focus:outline-none flex items-center shrink-0 ${isActive ? 'bg-[var(--theme-primary)]' : 'bg-slate-300'}`}
    >
      <div className={`absolute left-0.5 bg-white w-5 h-5 rounded-full shadow-sm transition-transform duration-300 ease-in-out ${isActive ? 'translate-x-6' : 'translate-x-0'}`}></div>
    </button>
  </div>
);

PremiumToggle.propTypes = {
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
};

export const NotificationSettingsTab = ({ org, showNotification }) => {
  const [settings, setSettings] = useState(org?.notificationSettings || {
    bookingConfirmation: true,
    bookingReminder: true,
    feedbackRequest: false,
    staffShiftAssignment: true,
  });



  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };



  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/org-settings/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationSettings: settings })
      });
      if (res.ok) {
        if (showNotification) showNotification('Notification settings saved successfully', 'success');
      } else {
        if (showNotification) showNotification('Failed to save settings', 'error');
      }
    } catch (err) {
      console.error(err);
      if (showNotification) showNotification('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="w-full flex flex-col p-2 sm:p-4 lg:p-5 animate-in fade-in duration-300 font-sans">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 shrink-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            Notification Preferences
          </h2>
          <p className="text-xs font-medium text-slate-500 mt-1 truncate max-w-[80vw]">
            Manage how and when your customers and staff receive automated updates via SMS and Email.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all active:scale-95 disabled:opacity-70 disabled:pointer-events-none"
          style={{ background: 'var(--theme-primary)' }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Settings Table List */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden divide-y divide-slate-100">
        
        {/* Table Header */}
        <div className="bg-slate-50 px-3 sm:px-5 py-2 flex items-center justify-between">
          <div className="flex-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Notification Rule</span>
          </div>
          <div className="shrink-0 w-[100px] text-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</span>
          </div>
        </div>

        {/* Row 1 */}
        <div 
          className="p-3 sm:px-5 sm:py-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
          onClick={() => toggle('bookingConfirmation')}
        >
          <div className="flex-1 pr-4">
            <h4 className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-[var(--theme-primary)] transition-colors">Booking Confirmations</h4>
            <p className="text-xs font-medium text-slate-500 leading-relaxed">
              Send an immediate confirmation SMS and Email to the customer as soon as their booking is created.
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            <PremiumToggle isActive={settings.bookingConfirmation} onClick={() => toggle('bookingConfirmation')} />
          </div>
        </div>

        {/* Row 2 */}
        <div 
          className="p-3 sm:px-5 sm:py-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
          onClick={() => toggle('bookingReminder')}
        >
          <div className="flex-1 pr-4">
            <h4 className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-[var(--theme-primary)] transition-colors">Appointment Reminders</h4>
            <p className="text-xs font-medium text-slate-500 leading-relaxed">
              Automatically send a friendly reminder to the customer 2 hours before their scheduled appointment time.
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            <PremiumToggle isActive={settings.bookingReminder} onClick={() => toggle('bookingReminder')} />
          </div>
        </div>

        {/* Row 3 */}
        <div 
          className="p-3 sm:px-5 sm:py-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
          onClick={() => toggle('feedbackRequest')}
        >
          <div className="flex-1 pr-4">
            <h4 className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-[var(--theme-primary)] transition-colors">Feedback Requests</h4>
            <p className="text-xs font-medium text-slate-500 leading-relaxed">
              Follow up with customers 24 hours after their service is marked completed to request a review or feedback.
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            <PremiumToggle isActive={settings.feedbackRequest} onClick={() => toggle('feedbackRequest')} />
          </div>
        </div>

        {/* Row 4 */}
        <div 
          className="p-3 sm:px-5 sm:py-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
          onClick={() => toggle('staffShiftAssignment')}
        >
          <div className="flex-1 pr-4">
            <h4 className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-[var(--theme-primary)] transition-colors">Staff Shift Alerts</h4>
            <p className="text-xs font-medium text-slate-500 leading-relaxed">
              Notify staff members when they are assigned a new shift or when their existing schedule is modified.
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            <PremiumToggle isActive={settings.staffShiftAssignment} onClick={() => toggle('staffShiftAssignment')} />
          </div>
        </div>
      </div>



    </div>
  );
};
